const DEFAULT_KEYS = [
  "summary",
  "description",
  "marketAnalysis",
  "productionPlan",
  "marketingPlan",
  "financialPlan",
  "risks",
  "socialImpact",
];

const createEmptyBuffers = (keys) => {
  const result = {};
  keys.forEach((key) => {
    result[key] = "";
  });
  return result;
};

const isWhitespace = (ch) => ch === " " || ch === "\n" || ch === "\r" || ch === "\t";

const ESCAPE_MAP = {
  n: "\n",
  r: "\r",
  t: "\t",
  '"': '"',
  "\\": "\\",
  "/": "/",
  b: "\b",
  f: "\f",
};

export const createEmptyPlanStream = (keys = DEFAULT_KEYS) => createEmptyBuffers(keys);

export const createPlanStreamParser = (keys = DEFAULT_KEYS) => {
  const keyList = [...keys];
  const keySet = new Set(keyList);

  const initBuffers = () => createEmptyBuffers(keyList);

  const state = {
    mode: "AWAIT_KEY",
    keyBuffer: "",
    keyEscape: false,
    currentKey: null,
    capturing: false,
    currentValue: "",
    valueEscape: "none",
    pendingUnicode: "",
    otherValueDepth: 0,
    buffers: initBuffers(),
    pendingEmit: "",
    hasPendingWord: false,
  };

  const reset = () => {
    state.mode = "AWAIT_KEY";
    state.keyBuffer = "";
    state.keyEscape = false;
    state.currentKey = null;
    state.capturing = false;
    state.currentValue = "";
    state.valueEscape = "none";
    state.pendingUnicode = "";
    state.otherValueDepth = 0;
    state.buffers = initBuffers();
    state.pendingEmit = "";
    state.hasPendingWord = false;
  };

  const hydrate = (buffers = {}, activeKey = null) => {
    reset();
    keyList.forEach((key) => {
      state.buffers[key] = typeof buffers[key] === "string" ? buffers[key] : "";
    });
    if (activeKey && keySet.has(activeKey)) {
      state.mode = "READ_STRING_VALUE";
      state.currentKey = activeKey;
      state.capturing = true;
      state.currentValue = state.buffers[activeKey] || "";
    }
  };

  const getBuffers = () => {
    const copy = {};
    keyList.forEach((key) => {
      copy[key] = state.buffers[key] || "";
    });
    return copy;
  };

  const processChunk = (chunk) => {
    if (!chunk) return [];

    const updates = [];
    const text = chunk;
    let i = 0;

    const pushUpdate = () => {
      if (!state.currentKey) return;
      const value = state.buffers[state.currentKey] || "";
      updates.push({ key: state.currentKey, value });
      // try {
      //   if (keySet.has(state.currentKey)) {
      //     console.log(
      //       `[Plan stream][parser][${state.currentKey}] emit (${value.length} символов)`
      //     );
      //   }
      // } catch (_) {}
    };

    const resetPendingEmit = () => {
      state.pendingEmit = "";
      state.hasPendingWord = false;
    };

    const flushPending = (force = false) => {
      if (!state.capturing || !state.currentKey) {
        resetPendingEmit();
        return;
      }
      if (!force) {
        if (!state.hasPendingWord) {
          return;
        }
        if (state.pendingEmit.trim().length === 0) {
          return;
        }
      } else if (!state.hasPendingWord && state.pendingEmit.trim().length === 0) {
        return;
      }
      pushUpdate();
      resetPendingEmit();
    };

    const appendValue = (value) => {
      if (!state.capturing) return;
      if (value === undefined || value === null || value === "") return;
      const str = String(value);
      if (!str) return;
      state.currentValue += str;
      state.buffers[state.currentKey] = state.currentValue;

      for (let j = 0; j < str.length; j += 1) {
        const ch = str[j];
        state.pendingEmit += ch;
        if (!isWhitespace(ch)) {
          state.hasPendingWord = true;
        }
        if (isWhitespace(ch)) {
          flushPending(false);
        }
      }
    };

    while (i < text.length) {
      const ch = text[i];

      if (state.mode === "READ_STRING_VALUE" && state.valueEscape === "unicode") {
        if (/^[0-9a-fA-F]$/.test(ch)) {
          state.pendingUnicode += ch;
          if (state.pendingUnicode.length === 6) {
            const hex = state.pendingUnicode.slice(2);
            const codePoint = parseInt(hex, 16);
            if (!Number.isNaN(codePoint)) {
              appendValue(String.fromCodePoint(codePoint));
            } else {
              appendValue(state.pendingUnicode);
            }
            state.valueEscape = "none";
            state.pendingUnicode = "";
          }
          i += 1;
          continue;
        }
        appendValue(state.pendingUnicode);
        state.valueEscape = "none";
        state.pendingUnicode = "";
        continue;
      }

      if (state.mode === "READ_STRING_VALUE" && state.valueEscape === "escape") {
        if (ch === "u") {
          state.valueEscape = "unicode";
          state.pendingUnicode = "\\u";
        } else {
          const mapped = ESCAPE_MAP[ch];
          appendValue(mapped !== undefined ? mapped : ch);
          state.valueEscape = "none";
        }
        i += 1;
        continue;
      }

      switch (state.mode) {
        case "AWAIT_KEY":
          if (ch === '"') {
            state.mode = "READ_KEY";
            state.keyBuffer = "";
            state.keyEscape = false;
          }
          i += 1;
          break;
        case "READ_KEY":
          if (state.keyEscape) {
            state.keyBuffer += ch;
            state.keyEscape = false;
          } else if (ch === "\\") {
            state.keyEscape = true;
          } else if (ch === '"') {
            state.currentKey = state.keyBuffer;
            state.mode = "AFTER_KEY";
          } else {
            state.keyBuffer += ch;
          }
          i += 1;
          break;
        case "AFTER_KEY":
          if (ch === ":") {
            state.mode = "AWAIT_VALUE";
          }
          i += 1;
          break;
        case "AWAIT_VALUE":
          if (ch === '"') {
            state.mode = "READ_STRING_VALUE";
            state.valueEscape = "none";
            state.pendingUnicode = "";
            state.capturing = keySet.has(state.currentKey);
            state.currentValue = state.capturing ? state.buffers[state.currentKey] || "" : "";
            if (state.capturing) {
              // Log when we detect the start of a section value for tracked keys
              // try {
              //   const existingLen = (state.buffers[state.currentKey] || "").length;
              //   if (existingLen === 0) {
              //     console.log(
              //       `[Plan stream][parser][${state.currentKey}] начало раздела`
              //     );
              //   }
              // } catch (_) {}
              resetPendingEmit();
            }
            i += 1;
          } else if (ch === "," || ch === "}") {
            state.currentKey = null;
            state.mode = "AWAIT_KEY";
            i += 1;
          } else if (isWhitespace(ch)) {
            i += 1;
          } else {
            state.capturing = false;
            state.currentValue = "";
            state.otherValueDepth = 0;
            state.mode = "READ_OTHER_VALUE";
            continue;
          }
          break;
        case "READ_STRING_VALUE":
          if (ch === "\\") {
            state.valueEscape = "escape";
            i += 1;
          } else if (ch === '"') {
            flushPending(true);
            state.mode = "EXPECT_DELIM";
            state.currentKey = null;
            state.capturing = false;
            state.currentValue = "";
            resetPendingEmit();
            i += 1;
          } else {
            appendValue(ch);
            i += 1;
          }
          break;
        case "EXPECT_DELIM":
          if (ch === ",") {
            state.mode = "AWAIT_KEY";
            i += 1;
          } else if (ch === "}") {
            state.mode = "AWAIT_KEY";
            i += 1;
          } else if (isWhitespace(ch)) {
            i += 1;
          } else {
            state.mode = "AWAIT_KEY";
            continue;
          }
          break;
        case "READ_OTHER_VALUE":
          if (state.otherValueDepth === 0 && (ch === "," || ch === "}")) {
            state.mode = "AWAIT_KEY";
            state.currentKey = null;
            i += 1;
          } else {
            if (ch === "{" || ch === "[") {
              state.otherValueDepth += 1;
            } else if ((ch === "}" || ch === "]") && state.otherValueDepth > 0) {
              state.otherValueDepth -= 1;
            }
            i += 1;
          }
          break;
        default:
          i += 1;
          break;
      }
    }

    if (state.capturing) {
      flushPending(false);
    }

    return updates;
  };

  return {
    processChunk,
    reset,
    hydrate,
    getBuffers,
    getActiveKey: () => (state.capturing ? state.currentKey : null),
  };
};

export const PLAN_STREAM_SECTION_KEYS = [...DEFAULT_KEYS];
